import { Flex } from "@chakra-ui/layout";
import { Button, Portal } from "@chakra-ui/react";
import React, { RefObject } from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { JsonEditor } from "src/scenes/admin/components/JsonEditor";
import { safeJsonParseWithSchema } from "src/utils/jsonValidation";
import { ImportSchoolsAndGradesForEnrollmentPeriodSchema } from "../schemas";
import { onImportCompleteCallback } from "../types";

type Props = {
  onBack: () => void;
  onComplete: onImportCompleteCallback;
  enrollmentPeriodId: string;
  actionBarRef: RefObject<HTMLDivElement>;
};
export const ImportSchoolsFromJSON: React.FC<Props> = ({
  onBack,
  onComplete,
  enrollmentPeriodId,
  actionBarRef,
}) => {
  const [json, setJson] = React.useState("");
  const [jsonSchema, setJsonSchema] = React.useState<object>({});

  const toast = useAvelaToast({});
  const [isImporting, setImporting] = React.useState(false);
  const service = FSQ.useImportSchoolsAndGradesForEnrollmentPeriod();

  const handleImport = React.useCallback(async () => {
    setImporting(true);
    toast.closeAll();

    try {
      if (json === "") {
        toast.error({
          title: "JSON is required",
          description: "Please provide JSON data to import",
        });
        return;
      }

      const importSchoolsAndGradesSchema = await import(
        "src/generated/import-enrollment-periods-schools-schema.json"
      );

      // Safely parse and validate JSON against schema before sending to API
      const parseResult =
        await safeJsonParseWithSchema<ImportSchoolsAndGradesForEnrollmentPeriodSchema>(
          json,
          importSchoolsAndGradesSchema
        );

      if (!parseResult.success || !parseResult.data) {
        toast.error({
          title: "Invalid JSON",
          description: "Please check your JSON format",
        });
        return;
      }

      const { data: result } = await service.mutateAsync({
        path: { enrollmentPeriodId: enrollmentPeriodId },
        body: { schools: parseResult.data },
      });

      if (!result || !result?.data) {
        toast.genericError();
        return;
      }

      if (result.data.errors.length > 0) {
        toast.error({
          title: "Some schools were not imported",
          description: "Some or all schools were not imported due to errors",
        });
        return onComplete({
          data: result.data.schools,
          errors: result.data.errors,
        });
      }

      // Success
      toast({
        title: "Imported from JSON",
        description: "Schools and grades imported from JSON successfully",
        status: "info",
        isClosable: true,
      });

      return onComplete({
        data: result.data.schools,
      });
    } catch (error: any) {
      if (error.error?.message) {
        toast.error({
          title: "Something went wrong",
          description:
            "Unable to import schools and grades. Please check your JSON and try again.",
        });
        console.error("Something went wrong during import from JSON", error);
      } else {
        toast.genericError();
      }
    } finally {
      setImporting(false);
    }
  }, [service, enrollmentPeriodId, json, onComplete, toast]);

  React.useEffect(() => {
    (async () => {
      try {
        // Import the pre-generated form template schema
        const schema = await import(
          "src/generated/import-enrollment-periods-schools-schema.json"
        );
        setJsonSchema(schema.default || schema);
      } catch (error) {
        console.error(error);
        setJsonSchema({});
      }
    })();
  }, []);

  return (
    <Flex direction="column" gap={6} grow="1" height="100%">
      <JsonEditor
        schema={jsonSchema}
        text={json}
        onChange={setJson}
        filename="enrollmentPeriodSchoolsAndGrades.json"
        editorHeight="fillParent"
      />
      <Portal containerRef={actionBarRef}>
        <Flex justifyContent="space-between">
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={() => {
              onBack();
            }}
          >
            Back
          </Button>
          <Button isLoading={isImporting} onClick={handleImport}>
            Import
          </Button>
        </Flex>
      </Portal>
    </Flex>
  );
};
